<template>
    <div class="flex mt-6 justify-between">
        <h1 class="text-2xl font-bold text-gray-800">Alerts</h1>
    </div>
    <div>
        <BasicTabs :tabs="tabs" :tab-key="'alerts/overview'" />
    </div>

    <!-- Upgrade Required Section -->
    <UpgradeRequired v-if="!hasAlertsAccess" />

    <!-- Main Content -->
    <div
        v-else
        class="overflow-hidden rounded-xl border border-gray-200 bg-white"
    >
        <div class="border-b border-gray-100 px-6 py-5">
            <h3 class="font-medium text-gray-900">Domains</h3>
            <p class="mt-1 text-sm text-gray-600">
                Monitor your domains and their alert status
            </p>
        </div>

        <div v-if="props.domains.length === 0" class="px-6 py-8">
            <div class="text-center">
                <div class="text-gray-400">
                    <svg
                        class="mx-auto h-12 w-12"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                    </svg>
                </div>
                <p class="mt-2 text-sm font-medium text-gray-900">
                    No domains found
                </p>
                <p class="text-sm text-gray-500">
                    Add domains to start monitoring alerts
                </p>
            </div>
        </div>

        <div v-else class="overflow-x-auto">
            <table class="w-full">
                <thead class="border-b border-gray-100 bg-gray-50">
                    <tr>
                        <th
                            class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                        >
                            Domain
                        </th>
                        <th
                            class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                        >
                            Status
                        </th>
                        <th
                            class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                        >
                            Current Compliance
                        </th>
                        <th
                            class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                        >
                            Previous Compliance
                        </th>
                        <th
                            class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                        >
                            Change
                        </th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                    <tr
                        v-for="domain in props.domains"
                        :key="domain.domain"
                        class="transition-colors duration-150 hover:bg-gray-50"
                    >
                        <td class="px-6 py-4">
                            <div class="flex items-center gap-3">
                                <div
                                    :class="[
                                        'h-2 w-2 rounded-full',
                                        domain.active
                                            ? 'bg-green-500'
                                            : 'bg-gray-400',
                                    ]"
                                ></div>
                                <span class="font-medium text-gray-900">
                                    {{ domain.domain }}
                                </span>
                            </div>
                        </td>
                        <td class="px-6 py-4">
                            <span
                                :class="[
                                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                                    domain.active
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800',
                                ]"
                            >
                                {{ domain.active ? "Active" : "Inactive" }}
                            </span>
                        </td>
                        <td class="px-6 py-4">
                            <div
                                v-if="domain.current_compliance !== undefined"
                                class="flex items-center gap-2"
                            >
                                <span
                                    :class="[
                                        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                                        domain.current_compliance >= 80
                                            ? 'bg-green-100 text-green-800'
                                            : domain.current_compliance >= 50
                                              ? 'bg-yellow-100 text-yellow-800'
                                              : 'bg-red-100 text-red-800',
                                    ]"
                                >
                                    {{ domain.current_compliance }}%
                                </span>
                                <span class="text-xs text-gray-500">
                                    ({{ domain.report_30?.sum || 0 }} emails)
                                </span>
                            </div>
                            <span v-else class="text-sm text-gray-400"
                                >No data</span
                            >
                        </td>
                        <td class="px-6 py-4">
                            <div
                                v-if="domain.previous_compliance !== undefined"
                                class="flex items-center gap-2"
                            >
                                <span
                                    :class="[
                                        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                                        domain.previous_compliance >= 80
                                            ? 'bg-green-100 text-green-800'
                                            : domain.previous_compliance >= 50
                                              ? 'bg-yellow-100 text-yellow-800'
                                              : 'bg-red-100 text-red-800',
                                    ]"
                                >
                                    {{ domain.previous_compliance }}%
                                </span>
                                <span class="text-xs text-gray-500">
                                    ({{ domain.report_30_before?.sum || 0 }}
                                    emails)
                                </span>
                            </div>
                            <span v-else class="text-sm text-gray-400"
                                >No data</span
                            >
                        </td>
                        <td class="px-6 py-4">
                            <div
                                v-if="
                                    domain.compliance_change !== null &&
                                    domain.compliance_change !== undefined
                                "
                                class="flex items-center gap-1"
                            >
                                <component
                                    :is="
                                        domain.compliance_change > 0
                                            ? TrendingUp
                                            : domain.compliance_change < 0
                                              ? TrendingDown
                                              : Minus
                                    "
                                    :class="[
                                        'h-4 w-4',
                                        domain.compliance_change > 0
                                            ? 'text-green-600'
                                            : domain.compliance_change < 0
                                              ? 'text-red-600'
                                              : 'text-gray-400',
                                    ]"
                                />
                                <span
                                    :class="[
                                        'text-sm font-medium',
                                        domain.compliance_change > 0
                                            ? 'text-green-600'
                                            : domain.compliance_change < 0
                                              ? 'text-red-600'
                                              : 'text-gray-600',
                                    ]"
                                >
                                    {{ domain.compliance_change > 0 ? "+" : ""
                                    }}{{ domain.compliance_change }}%
                                </span>
                            </div>
                            <span v-else class="text-sm text-gray-400"
                                >No data</span
                            >
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script lang="ts" setup>
import BasicTabs from "@/components/Atomic/Tabs/BasicTabs.vue";
import UpgradeRequired from "@/components/Atomic/Miscellaneous/UpgradeRequired.vue";
import useAlertsAccess from "@/composables/useAlertsAccess";
import {
    Bell,
    Settings,
    TrendingUp,
    TrendingDown,
    Minus,
} from "lucide-vue-next";

import type TabItem from "@/types/Settings/TabItem";
import type IDomain from "@/types/Domains/IDomain";

const { hasAlertsAccess } = useAlertsAccess();

interface Props {
    domains: IDomain[];
}

const props = defineProps<Props>();

const tabs: TabItem[] = [
    {
        title: "Overview",
        key: "alerts/overview",
        icon: Bell,
        url: "/alerts/overview",
    },
    {
        title: "Settings",
        key: "settings",
        icon: Settings,
        url: "/alerts/settings",
    },
];
</script>
